import type React from "react";
import { accessAllows, type NavUser } from "@domain/Auth";
import { NAV_MENUS, ROUTE_DEFS, type NavMenuId, type NavSection, type RouteDef } from "./routeTable";

/** A rendered link: what the sidebar, Home grid and account menu draw. */
export type NavLink = {
	to: string;
	text: string;
	icon?: React.ReactNode;
};

/** One row of a nav section: a link, or a collapsible menu of links. */
export type NavItem =
	| { kind: "link"; link: NavLink }
	| { kind: "menu"; id: NavMenuId; label: string; icon: React.ReactNode; links: NavLink[] };

type NavRoute = (typeof ROUTE_DEFS)[number] & { nav: NonNullable<RouteDef["nav"]> };

const NAV_ROUTES: readonly NavRoute[] = ROUTE_DEFS.filter((r): r is NavRoute => r.nav !== undefined);

const toLink = (r: NavRoute): NavLink => ({ to: r.path, text: r.nav.label ?? r.title, icon: r.nav.icon });

function visibleRoutes(section: NavSection, user: NavUser | undefined): NavRoute[] {
	return NAV_ROUTES.filter((r) => r.nav.section === section && accessAllows(r.nav.access ?? r.access, user)).sort(
		(a, b) => a.nav.order - b.nav.order,
	);
}

/** The section's rows in sidebar order; a menu takes the position of its first member. Empty
 *  when the user can see nothing in the section. */
export function navItems(section: NavSection, user: NavUser | undefined): NavItem[] {
	const items: NavItem[] = [];
	const menus = new Map<NavMenuId, Extract<NavItem, { kind: "menu" }>>();
	for (const route of visibleRoutes(section, user)) {
		const menuId = route.nav.menu;
		if (!menuId) {
			items.push({ kind: "link", link: toLink(route) });
			continue;
		}
		let menu = menus.get(menuId);
		if (!menu) {
			menu = { kind: "menu", id: menuId, ...NAV_MENUS[menuId], links: [] };
			menus.set(menuId, menu);
			items.push(menu);
		}
		menu.links.push(toLink(route));
	}
	return items;
}

/** The section's links flattened for surfaces without submenus (the Home grid, the account menu):
 *  the section's own links first, then each menu's links in turn. */
export function navLinks(section: NavSection, user: NavUser | undefined): NavLink[] {
	const items = navItems(section, user);
	return [
		...items.flatMap((item) => (item.kind === "link" ? [item.link] : [])),
		...items.flatMap((item) => (item.kind === "menu" ? item.links : [])),
	];
}

/** Every path the user has a link to, in any section — what the issue-count badges may cover. */
export function visibleNavPaths(user: NavUser | undefined): Set<string> {
	const sections: NavSection[] = ["main", "admin", "developer", "account"];
	return new Set(sections.flatMap((section) => navLinks(section, user).map((link) => link.to)));
}
